import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { AlertOctagon, ArrowLeft, CheckCircle2, ChevronRight, Flag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

function fmt(iso) { return iso ? new Date(iso).toLocaleString() : "—"; }
function statusPill(status) {
  const cls = status === "pending" ? "bg-danger-50 text-danger-700"
    : status === "cleared" ? "bg-success-50 text-success-700"
    : "bg-warning-50 text-warning-700";
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cls}`}>{status}</span>;
}

export function WatchlistHits() {
  const [hits, setHits] = useState([]);
  const [filter, setFilter] = useState("pending");

  const refresh = async () => {
    try {
      const { data } = await api.get("/watchlist-hits", { params: filter !== "all" ? { status: filter } : {} });
      setHits(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); }, [filter]);

  return (
    <AppShell>
      <div className="space-y-6" data-testid="watchlist-hits-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-navy-900 tracking-tight flex items-center gap-2">
              <AlertOctagon className="w-7 h-7 text-danger-500" /> Watchlist hits
            </h1>
            <p className="text-sm text-navy-500 mt-1">
              Discreet block events. Visitors are turned back to reception with no indication of being on a list.
            </p>
          </div>
          <div className="flex items-center gap-1 rounded-full bg-navy-50 p-1">
            {["pending", "cleared", "escalated", "all"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  filter === s ? "bg-white text-navy-900 shadow-sm" : "text-navy-500 hover:text-navy-900"
                }`}
                data-testid={`hits-filter-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {hits.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-navy-200 p-10 text-center text-navy-500">
            No {filter === "all" ? "" : filter} watchlist hits.
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-navy-100 overflow-hidden">
            <ul className="divide-y divide-navy-100">
              {hits.map((h) => (
                <li key={h.id}>
                  <Link to={`/admin/watchlist-hits/${h.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-navy-50 transition-colors" data-testid={`hit-row-${h.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-navy-900">{(h.attempted_visitor || {}).name || "Unknown"}</div>
                      <div className="text-xs text-navy-500">
                        {fmt(h.hit_at)} · matched <span className="font-mono">{h.entry_match_type}</span> against <em>"{h.entry_name || "—"}"</em>
                      </div>
                    </div>
                    {statusPill(h.status || "pending")}
                    <ChevronRight className="w-4 h-4 text-navy-300" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </AppShell>
  );
}

export function WatchlistHitDetail() {
  const { hitId } = useParams();
  const [hit, setHit] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/watchlist-hits/${hitId}`);
      setHit(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); }, [hitId]);

  const resolve = async (status) => {
    const note = window.prompt(status === "cleared" ? "Note (false-positive justification):" : "Note (escalation reason):");
    if (!note) return;
    setBusy(true);
    try {
      const path = status === "cleared" ? "mark-cleared" : "mark-escalated";
      await api.post(`/watchlist-hits/${hitId}/${path}`, { note });
      toast.success(status === "cleared" ? "Cleared" : "Escalated");
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!hit) return <AppShell><div className="text-navy-500">Loading…</div></AppShell>;
  const av = hit.attempted_visitor || {};
  return (
    <AppShell>
      <div className="space-y-5 max-w-3xl" data-testid="watchlist-hit-detail">
        <Link to="/admin/watchlist-hits" className="inline-flex items-center gap-1 text-sm text-navy-500 hover:text-navy-900">
          <ArrowLeft className="w-4 h-4" /> Back to hits
        </Link>
        <div className="rounded-2xl bg-white border border-danger-200 p-6 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-danger-600 font-bold">Watchlist match</div>
              <div className="text-2xl font-bold text-navy-900 mt-0.5">{av.name || "(no name)"}</div>
              <div className="text-xs text-navy-500 mt-1">{fmt(hit.hit_at)}</div>
            </div>
            {statusPill(hit.status || "pending")}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-navy-500 text-xs">Email</span><div>{av.email || "—"}</div></div>
            <div><span className="text-navy-500 text-xs">Phone</span><div>{av.phone || "—"}</div></div>
            <div><span className="text-navy-500 text-xs">Company</span><div>{av.company || "—"}</div></div>
            <div><span className="text-navy-500 text-xs">Type</span><div>{av.visitor_type || "—"}</div></div>
          </div>
          <div className="border-t border-navy-100 pt-3">
            <div className="text-xs text-navy-500">Watchlist entry name</div>
            <div className="font-medium text-navy-900">{hit.entry_name || "—"} <span className="font-mono text-[11px] text-navy-500">({hit.entry_match_type})</span></div>
            <div className="text-xs text-navy-500 mt-2">Reason</div>
            <div className="text-sm text-navy-800">{hit.entry_reason || "—"}</div>
          </div>
          {hit.status === "pending" ? (
            <div className="flex gap-2 pt-2">
              <Button onClick={() => resolve("cleared")} disabled={busy} variant="outline" data-testid="hit-mark-cleared">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark cleared (false positive)
              </Button>
              <Button onClick={() => resolve("escalated")} disabled={busy} className="bg-danger-600 hover:bg-danger-600/90 text-white" data-testid="hit-mark-escalated">
                <Flag className="w-4 h-4 mr-1.5" /> Escalate
              </Button>
            </div>
          ) : (
            <div className="border-t border-navy-100 pt-3">
              <div className="text-xs text-navy-500">Resolution</div>
              <div className="text-sm text-navy-800">{hit.resolution_note || "—"}</div>
              <div className="text-xs text-navy-400 mt-1">By {hit.resolved_by_user_id} at {fmt(hit.resolved_at)}</div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
